import React from 'react';
import { Settings } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Separator } from '../../components/ui/separator';

const CATEGORY_COLORS = [
  '#006400', '#16A34A', '#0EA5A4', '#F2C94C', '#E67E22',
  '#B91C1C', '#7C3AED', '#2563EB', '#64748B', '#D946EF',
  '#14B8A6', '#F97316',
];

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white border rounded-lg shadow-sm p-3 text-sm">
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color || entry.payload?.fill }}>
          {entry.name}: ${Number(entry.value).toLocaleString('es-ES', { minimumFractionDigits: 2 })}
        </p>
      ))}
    </div>
  );
};

/**
 * Sección central: gráfica de torta de costos + resumen de configuración del lote.
 */
export const DistribucionYConfig = ({ resumen, config, onOpenConfig }) => {
  const pieData = resumen?.por_categoria
    ? Object.entries(resumen.por_categoria).map(([name, value]) => ({ name, value }))
    : [];

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
      <Card className="xl:col-span-2">
        <CardHeader>
          <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk' }}>
            Distribucion de Costos por Categoria
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%" cy="50%"
                  innerRadius={70} outerRadius={120}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {pieData.map((_, i) => (
                    <Cell key={i} fill={CATEGORY_COLORS[i % CATEGORY_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[320px] text-muted-foreground">
              <p>Sin gastos registrados aun</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk' }}>
            Configuracion del Lote
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Valor granja:</span>
            <span className="font-medium tabular-nums">
              ${config?.valor_granja?.toLocaleString('es-ES') || 0}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Vida util:</span>
            <span className="font-medium tabular-nums">{config?.vida_util_anos || 0} años</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Lotes depreciando:</span>
            <span className="font-medium tabular-nums">{config?.num_lotes_depreciar || 1}</span>
          </div>
          <Separator />
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Depreciacion mensual:</span>
            <span className="font-semibold tabular-nums text-amber-600">
              ${config?.depreciacion_mensual?.toLocaleString('es-ES', { minimumFractionDigits: 2 }) || '0.00'}
            </span>
          </div>
          {resumen && (
            <>
              <Separator />
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Gallinas iniciales:</span>
                <span className="font-medium tabular-nums">
                  {resumen.total_gallinas_iniciales?.toLocaleString('es-ES')}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Gallinas actuales:</span>
                <span className="font-medium tabular-nums">
                  {resumen.total_gallinas?.toLocaleString('es-ES')}
                </span>
              </div>
            </>
          )}
          <Button variant="outline" className="w-full mt-2" size="sm" onClick={onOpenConfig}>
            <Settings className="w-4 h-4 mr-2" /> Editar Configuracion
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
